import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog.tsx';
import { Button } from '@/components/ui/button.tsx';
import { Input } from '@/components/ui/input.tsx';
import { Label } from '@/components/ui/label.tsx';
import { Textarea } from '@/components/ui/textarea.tsx';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select.tsx';
import { FunnelFormData } from '@/types/funnel.ts';
import { ChevronDown, ChevronUp, Link2 } from 'lucide-react';
import FunnelPageSelector from './FunnelPageSelector.tsx';
import { getFunnelCategories } from '@/services/api';

interface CreateFunnelModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onSubmit: (data: FunnelFormData) => void;
    isLoading: boolean;
}

const emptyForm = (): FunnelFormData => ({
    nome: '',
    page_ids: [],
    descricao: '',
    utm_source: '',
    utm_medium: '',
    utm_campaign: '',
});

export default function CreateFunnelModal({ open, onOpenChange, onSubmit, isLoading }: CreateFunnelModalProps) {
    const [formData, setFormData] = useState<FunnelFormData>(emptyForm());
    const [showUtm, setShowUtm] = useState(false);

    const { data: categories = [] } = useQuery({
        queryKey: ['funnel-categories'],
        queryFn: getFunnelCategories,
        staleTime: 1000 * 60,
    });

    useEffect(() => {
        if (!open) {
            setFormData(emptyForm());
            setShowUtm(false);
        }
    }, [open]);

    const handleClose = () => {
        setFormData(emptyForm());
        setShowUtm(false);
        onOpenChange(false);
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        onSubmit(formData);
    };

    const canSubmit = formData.nome.trim() && formData.page_ids.length > 0;

    return (
        <Dialog open={open} onOpenChange={handleClose}>
            <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="text-lg sm:text-xl">Criar Novo Funil</DialogTitle>
                </DialogHeader>

                <form onSubmit={handleSubmit} className="space-y-5 mt-4">
                    {/* Nome */}
                    <div className="space-y-2">
                        <Label htmlFor="nome" className="text-sm font-medium">Nome do Funil *</Label>
                        <Input
                            id="nome"
                            value={formData.nome}
                            onChange={e => setFormData({ ...formData, nome: e.target.value })}
                            placeholder="Ex: Funil de Vendas"
                            required
                            disabled={isLoading}
                        />
                    </div>

                    {/* Páginas */}
                    <div className="space-y-2">
                        <Label className="text-sm font-medium">Páginas do Facebook *</Label>
                        <FunnelPageSelector
                            selectedIds={formData.page_ids}
                            onChange={ids => setFormData(prev => ({ ...prev, page_ids: ids }))}
                        />
                    </div>

                    {/* Descrição */}
                    <div className="space-y-2">
                        <Label htmlFor="descricao" className="text-sm font-medium">Descrição</Label>
                        <Textarea
                            id="descricao"
                            value={formData.descricao}
                            onChange={e => setFormData({ ...formData, descricao: e.target.value })}
                            placeholder="Descreva o objetivo deste funil..."
                            rows={3}
                            disabled={isLoading}
                            className="resize-none"
                        />
                    </div>

                    {/* Categoria */}
                    {categories.length > 0 && (
                        <div className="space-y-2">
                            <Label className="text-sm font-medium">Categoria</Label>
                            <Select
                                value={formData.internal_category_id != null ? String(formData.internal_category_id) : 'none'}
                                onValueChange={(value) =>
                                    setFormData({ ...formData, internal_category_id: value === 'none' ? undefined : Number(value) })
                                }
                            >
                                <SelectTrigger disabled={isLoading}>
                                    <SelectValue placeholder="Sem categoria" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="none">Sem categoria</SelectItem>
                                    {categories.map((category) => (
                                        <SelectItem key={category.id} value={String(category.id)}>
                                            {category.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    )}

                    {/* UTM */}
                    <div className="space-y-3">
                        <button
                            type="button"
                            onClick={() => setShowUtm(!showUtm)}
                            className="flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors"
                        >
                            <Link2 className="h-4 w-4" />
                            Parâmetros UTM
                            {showUtm ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                        </button>

                        {showUtm && (
                            <div className="space-y-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                                <p className="text-xs text-gray-500">
                                    Parâmetros UTM serão adicionados automaticamente às URLs dos botões no fluxo.
                                </p>
                                {(['utm_source', 'utm_medium', 'utm_campaign'] as const).map(key => (
                                    <div key={key} className="space-y-1">
                                        <Label className="text-sm">{key}</Label>
                                        <Input
                                            value={formData[key] || ''}
                                            onChange={e => setFormData({ ...formData, [key]: e.target.value })}
                                            placeholder={key === 'utm_source' ? 'Ex: facebook' : key === 'utm_medium' ? 'Ex: chatbot' : 'Ex: vendas_janeiro'}
                                            disabled={isLoading}
                                        />
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Actions */}
                    <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-4 border-t">
                        <Button type="button" variant="outline" onClick={handleClose} disabled={isLoading} className="w-full sm:w-auto">
                            Cancelar
                        </Button>
                        <Button type="submit" disabled={isLoading || !canSubmit} className="w-full sm:w-auto bg-[#0f172a] hover:bg-[#0a1120] text-white">
                            {isLoading ? (
                                <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />Criando...</>
                            ) : 'Criar Funil'}
                        </Button>
                    </div>
                </form>
            </DialogContent>
        </Dialog>
    );
}
